import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import service from '@/services';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { SOURCE_TYPE } from '@/types/feedbackConst';

import { getIntegrationQueryKeys } from '../../integrationUtils';
import { IMPORT_STATE, type KnocommerceInfo, type KnocommerceWorkspaceT } from '../types';

type ConnectParams = {
  apiKey: string;
  apiSecret: string;
};

const normalizeKnocommerceInfo = (
  workspace: KnocommerceWorkspaceT | KnocommerceInfo,
): KnocommerceInfo => {
  return {
    ...workspace,
    history: workspace.history || [],
    workspace_id:
      (workspace as (KnocommerceWorkspaceT | KnocommerceInfo) & {
        workspace_id?: string;
      })
        .workspace_id || workspace.alias,
  };
};

const mapConnectErrorMessage = (e: unknown): string => {
  if (e instanceof AxiosError && e.response) {
    if (e.response.status === 409) {
      return 'The workspace with this API key is already connected to Syncly. Please check again!';
    }
    if (e.response.status === 403) {
      return 'Your API key and secret are invalid. Please check again!';
    }
    return 'Unknown error occurred while connecting to Knocommerce.';
  }

  return 'Failed to connect to Knocommerce. Please try again.';
};

export const useKnocommerce = (enabled: boolean) => {
  const queryClient = useQueryClient();
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const workspaceAlias = currWorkspace?.alias;
  if (!workspaceAlias) throw new Error('Workspace alias is required');

  const queryKeys = getIntegrationQueryKeys(
    SOURCE_TYPE.KNOCOMMERCE,
    workspaceAlias,
  );

  const { data: knocommerceWorkspace } = useQuery({
    queryKey: queryKeys,
    queryFn: async () => {
      const data = await service.knocommerce.getWorkspaceInfo(workspaceAlias);
      return normalizeKnocommerceInfo(data);
    },
    enabled,
  });

  const { mutateAsync: connectMutation } = useMutation({
    mutationFn: async ({ apiKey, apiSecret }: ConnectParams) => {
      try {
        const workspace = await service.knocommerce.createWorkspaceInfo(
          apiKey,
          apiSecret,
          workspaceAlias,
        );
        return normalizeKnocommerceInfo(workspace);
      } catch (e) {
        throw new Error(mapConnectErrorMessage(e));
      }
    },
    onSuccess: (workspace) => {
      queryClient.setQueryData(queryKeys, workspace);
    },
  });

  const { mutate: disconnectMutation } = useMutation({
    mutationFn: () => service.knocommerce.deleteWorkspaceInfo(workspaceAlias),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys });
    },
  });

  const { mutate: requestImportMutation } = useMutation({
    mutationFn: (dateRange: NullableDateRangeT) =>
      service.knocommerce.importWorkspaceFeedback(
        workspaceAlias,
        dateRange.start_date || '',
        dateRange.end_date || '',
      ),
    onSuccess: () => {
      queryClient.setQueryData(
        queryKeys,
        (old: KnocommerceInfo | null | undefined) => {
          if (!old) return old;
          return {
            ...old,
            import_state: IMPORT_STATE.IMPORTING,
          };
        },
      );
    },
  });

  return {
    knocommerceWorkspace,
    connectMutation,
    disconnectMutation,
    requestImportMutation,
  };
};
